/**
 * The minimap: the level's rooms as a small plan, the visitor's position on it, and the
 * navigation a tap on a room triggers — the app's `GUI/Minimap` (Minimap.cs,
 * MinimapRooms.cs, MinimapIndicator.cs) in plain data.
 *
 * Conventions copied from the app: the active room is blue, the others white; the
 * surrounding outdoor room (the one every other room sits inside) is left off the map so
 * it does not dwarf the plan; the indicator is the camera's XZ clamped into the active
 * room along a ray from a point known to be inside, so a visitor standing in a doorway is
 * still shown; the button appears from two rooms up.
 */
import type { Level, Room, Vec2 } from '../core/hp/types';
import { containsPoint } from './sceneGesture';
import { interiorPoint } from './placement';

export const MINIMAP_ACTIVE_COLOR = '#b5d6f6';
export const MINIMAP_ROOM_COLOR = '#ffffff';
export const MINIMAP_BORDER_COLOR = '#000000';
export const MINIMAP_HIDDEN_BORDER_COLOR = '#d7d7d7';
export const MINIMAP_MIN_ROOMS = 2;

const OUTDOOR = /outdoor|garden|yard|terrace|balcony|patio|street|exterior|landscape|backyard/i;

export interface MinimapLayout {
  /** Minimap pixels per plan metre. */
  scale: number;
  offsetX: number;
  offsetY: number;
  width: number;
  height: number;
}

/** Rooms the map shows: every drawn room except a surrounding outdoor one. */
export function minimapRooms(level: Pick<Level, 'rooms'> | null | undefined): Room[] {
  const rooms = (level?.rooms ?? []).filter((room) => room.polygon.length >= 3);
  return rooms.filter((room) => !isSurroundingOutdoor(room, rooms));
}

function isSurroundingOutdoor(room: Room, rooms: Room[]): boolean {
  if (!OUTDOOR.test(room.roomType ?? '') && !OUTDOOR.test(room.name ?? '')) return false;
  const others = rooms.filter((other) => other !== room);
  if (!others.length) return false;
  const nested = others.filter((other) => containsPoint(room.polygon, labelCenter(other.polygon)));
  return nested.length >= others.length;
}

/** Where a room's name sits: the interior point farthest from the walls. */
export function labelCenter(polygon: Vec2[]): Vec2 {
  return interiorPoint(polygon);
}

/** Fit every room into `maxWidth` × `maxHeight` keeping the plan's proportions, centred. */
export function minimapLayout(rooms: Room[], maxWidth: number, maxHeight: number, padding = 10): MinimapLayout {
  const points = rooms.flatMap((room) => room.polygon);
  if (!points.length) return { scale: 1, offsetX: 0, offsetY: 0, width: maxWidth, height: maxHeight };
  const xs = points.map((p) => p.x), zs = points.map((p) => p.z);
  const minX = Math.min(...xs), maxX = Math.max(...xs), minZ = Math.min(...zs), maxZ = Math.max(...zs);
  const spanX = Math.max(maxX - minX, 1e-3), spanZ = Math.max(maxZ - minZ, 1e-3);
  const scale = Math.min((maxWidth - 2 * padding) / spanX, (maxHeight - 2 * padding) / spanZ);
  const width = spanX * scale + 2 * padding, height = spanZ * scale + 2 * padding;
  return { scale, offsetX: width / 2 - ((minX + maxX) / 2) * scale, offsetY: height / 2 - ((minZ + maxZ) / 2) * scale, width, height };
}

/** Plan point → minimap pixel; plan z grows downwards, exactly like the 2D plan. */
export function toMinimap(point: Vec2, layout: MinimapLayout): { x: number; y: number } {
  return { x: layout.offsetX + point.x * layout.scale, y: layout.offsetY + point.z * layout.scale };
}

/**
 * A point outside the polygon is pulled inside along the ray from `anchor` (known to be
 * inside) to the boundary, stopping just short of it; an inside point is returned as is.
 */
export function clampPointIntoPolygon(point: Vec2, anchor: Vec2, polygon: Vec2[]): Vec2 {
  if (containsPoint(polygon, point)) return point;
  if (!containsPoint(polygon, anchor)) return anchor;
  let inside = 0, outside = 1;
  for (let i = 0; i < 16; i++) {
    const mid = (inside + outside) / 2;
    const probe = { x: anchor.x + (point.x - anchor.x) * mid, z: anchor.z + (point.z - anchor.z) * mid };
    if (containsPoint(polygon, probe)) inside = mid; else outside = mid;
  }
  return { x: anchor.x + (point.x - anchor.x) * inside, z: anchor.z + (point.z - anchor.z) * inside };
}

/** The room a point is in, else the nearest room by label centre, else the largest. */
export function activeRoom(rooms: Room[], point: Vec2 | null): Room | null {
  if (!rooms.length) return null;
  if (point) {
    const inside = rooms.find((room) => containsPoint(room.polygon, point));
    if (inside) return inside;
    let best: Room | null = null, bestDistance = Infinity;
    for (const room of rooms) {
      const centre = labelCenter(room.polygon);
      const distance = Math.hypot(centre.x - point.x, centre.z - point.z);
      if (distance < bestDistance) { best = room; bestDistance = distance; }
    }
    return best;
  }
  return rooms.reduce((largest, room) => (room.area > largest.area ? room : largest), rooms[0]);
}

/** Where the "I am here" arrow sits: the camera clamped into the active room. */
export function indicatorPoint(position: Vec2, room: Room, anchor?: Vec2): Vec2 {
  return clampPointIntoPolygon(position, anchor ?? labelCenter(room.polygon), room.polygon);
}

/** CSS rotation (degrees, clockwise) for an arrow drawn pointing up, from the camera's plan heading. */
export function indicatorAngle(forward: Vec2): number {
  return (Math.atan2(forward.x, -forward.z) * 180) / Math.PI;
}

/** The walls of a room: those with an end on (or just inside) its outline, for isolation. */
export function wallsOfRoom(room: Room, walls: Level['walls'], tolerance = 0.3): Level['walls'] {
  const near = (p: Vec2) => containsPoint(room.polygon, p) || distanceToPolygon(p, room.polygon) <= tolerance;
  return walls.filter((wall) => near(wall.start) || near(wall.end) || near(wall.center));
}

function distanceToPolygon(p: Vec2, polygon: Vec2[]): number {
  let best = Infinity;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const a = polygon[j], b = polygon[i];
    const dx = b.x - a.x, dz = b.z - a.z;
    const length2 = dx * dx + dz * dz;
    const t = length2 > 0 ? Math.max(0, Math.min(1, ((p.x - a.x) * dx + (p.z - a.z) * dz) / length2)) : 0;
    best = Math.min(best, Math.hypot(p.x - (a.x + dx * t), p.z - (a.z + dz * t)));
  }
  return best;
}
